import { useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { isSupportedLocale, pickDefaultLocale } from "@/i18n/locale-utils";
import { useEntitlements } from "@/hooks/useEntitlements";

/**
 * Gates AI mentor surfaces (KORA, Velyra, Strategist) behind a Founder Pass
 * or Coaching subscription. Anonymous users → /login. Authed users without
 * an entitlement → /mentoring/hub which surfaces the upgrade CTA.
 */
export function RequireAiMentor({ children }: { children: ReactNode }) {
  const [authed, setAuthed] = useState<null | boolean>(null);
  const { pathname, search, hash } = useLocation();
  const { locale: paramLocale } = useParams<{ locale: string }>();
  const locale = isSupportedLocale(paramLocale) ? paramLocale : pickDefaultLocale();
  const { loading, canUseAiMentors } = useEntitlements();

  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (mounted) setAuthed(!!data.user);
    })();
    return () => { mounted = false; };
  }, []);

  if (authed === null || loading) {
    return (
      <div className="grid min-h-screen place-items-center text-sm text-muted-foreground">Loading…</div>
    );
  }
  if (!authed) {
    const dest = `${pathname}${search}${hash}`;
    return <Navigate to={`/${locale}/login?next=${encodeURIComponent(dest)}`} replace />;
  }
  if (!canUseAiMentors) {
    return <Navigate to={`/${locale}/mentoring/hub?need=ai-mentors`} replace />;
  }
  return <>{children}</>;
}